import { useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import { useNavigate } from '@tanstack/react-router';
import {
  useCancelTraining,
  useRequeueTraining,
  useTrainingQueue,
  type TrainingQueueItemDto,
} from '../api/queries/lora';
import { FilterableSelect } from '../components/common/FilterableSelect';

const ENTITY_TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'Character', label: 'Character' },
  { value: 'Location', label: 'Location' },
  { value: 'Item', label: 'Item' },
  { value: 'Creature', label: 'Creature' },
  { value: 'Artifact', label: 'Artifact' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'Queued', label: 'Queued' },
  { value: 'Training', label: 'Training' },
  { value: 'Complete', label: 'Complete' },
  { value: 'Failed', label: 'Failed' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'Approved', label: 'Approved' },
];

const STATUS_COLORS: Record<
  string,
  'default' | 'primary' | 'success' | 'error' | 'warning'
> = {
  queued: 'default',
  training: 'primary',
  complete: 'success',
  failed: 'error',
  approved: 'success',
  cancelled: 'default',
};

const PHASE_LABELS: Record<string, string> = {
  download: 'Downloading',
  caption: 'Captioning',
  configure: 'Configuring',
  training: 'Training',
  bundling: 'Bundling',
  uploading: 'Uploading',
};

export function TrainingQueueRoute() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [entityType, setEntityType] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const queue = useTrainingQueue({
    status: status || undefined,
    entityType: entityType || undefined,
    page: page + 1,
    pageSize,
  });

  return (
    <Stack spacing={2} sx={{ p: 3 }}>
      <Typography variant="h5">Training Queue</Typography>

      <Stack direction="row" spacing={2} alignItems="center">
        <Box sx={{ width: 200 }}>
          <FilterableSelect
            options={STATUS_OPTIONS}
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(0);
            }}
            label="Status"
            allowEmpty={false}
          />
        </Box>
        <Box sx={{ width: 200 }}>
          <FilterableSelect
            options={ENTITY_TYPE_OPTIONS}
            value={entityType}
            onChange={(v) => {
              setEntityType(v);
              setPage(0);
            }}
            label="Entity Type"
            allowEmpty={false}
          />
        </Box>
        {queue.data && (
          <Typography variant="body2" color="text.secondary">
            {queue.data.total} job{queue.data.total !== 1 ? 's' : ''}
          </Typography>
        )}
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Entity</TableCell>
              <TableCell>Trigger Word</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Worker</TableCell>
              <TableCell>Queued</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {queue.data?.items.map((item) => (
              <QueueRow
                key={item.id}
                item={item}
                onViewEntity={() =>
                  navigate({
                    to: '/entities/$entityId',
                    params: { entityId: item.entityId },
                  })
                }
                onRefresh={() => void queue.refetch()}
              />
            ))}
            {queue.data?.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 4 }}
                  >
                    No training jobs found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={queue.data?.total ?? 0}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          setPageSize(Number(e.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </Stack>
  );
}

function QueueRow({
  item,
  onViewEntity,
  onRefresh,
}: {
  item: TrainingQueueItemDto;
  onViewEntity: () => void;
  onRefresh: () => void;
}) {
  const cancel = useCancelTraining(item.entityId);
  const requeue = useRequeueTraining(item.entityId);
  const isActive =
    item.trainingStatus === 'training' || item.trainingStatus === 'queued';
  const isFailed =
    item.trainingStatus === 'failed' || item.trainingStatus === 'cancelled';

  return (
    <TableRow hover>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          {item.referenceImageUrl && (
            <Avatar
              src={item.referenceImageUrl}
              variant="rounded"
              sx={{ width: 32, height: 32 }}
            />
          )}
          <Box>
            <Typography variant="body2">{item.entityName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {item.entityType}
            </Typography>
          </Box>
        </Stack>
      </TableCell>
      <TableCell>
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
          {item.triggerWord}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={item.trainingStatus}
          color={STATUS_COLORS[item.trainingStatus] ?? 'default'}
          size="small"
        />
      </TableCell>
      <TableCell sx={{ minWidth: 180 }}>
        {item.trainingStatus === 'training' ? (
          <Stack spacing={0.5}>
            <Typography variant="caption">
              {item.currentPhase
                ? (PHASE_LABELS[item.currentPhase] ?? item.currentPhase)
                : '...'}
            </Typography>
            <LinearProgress
              variant={item.progress != null ? 'determinate' : 'indeterminate'}
              value={(item.progress ?? 0) * 100}
              sx={{ height: 6, borderRadius: 3 }}
            />
            {item.etaSeconds != null && item.etaSeconds > 0 && (
              <Typography variant="caption" color="text.secondary">
                ETA: {formatEta(item.etaSeconds)}
              </Typography>
            )}
          </Stack>
        ) : item.trainingStatus === 'queued' ? (
          <Typography variant="caption" color="text.secondary">
            Waiting...
          </Typography>
        ) : null}
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {item.claimedByWorker ?? '\u2014'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {new Date(item.createdAt).toLocaleString()}
        </Typography>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0}>
          {isActive && (
            <Tooltip title="Cancel training">
              <IconButton
                size="small"
                disabled={cancel.isPending}
                onClick={async () => {
                  await cancel.mutateAsync(item.id);
                  onRefresh();
                }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {isFailed && (
            <Tooltip title="Requeue">
              <IconButton
                size="small"
                disabled={requeue.isPending}
                onClick={async () => {
                  await requeue.mutateAsync(item.id);
                  onRefresh();
                }}
              >
                <ReplayIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="View entity">
            <IconButton size="small" onClick={onViewEntity}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

function formatEta(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ${seconds % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}
